import React, { useState, useMemo } from 'react'
import classNames from 'classnames'
import { useField } from 'formik'
import Text from '../../Text/Text'
import FormError from '../FormError'
import FormFieldSecretToggle from './FormFieldSecretToggle'
import { Props } from './types'
import { renderInput, renderFileInput } from './utils'

const FormField: React.FC<Props> = ({
  name,
  displayName,
  type,
  helpText,
  isSecret = false,
  prefix,
  className,
  ...rest
}) => {
  const [field, meta] = useField(name)
  const [isSecretMasked, setSecretMasked] = useState(isSecret)
  const hasError = useMemo(
    () => meta.touched && !!meta.error,
    [meta.touched, meta.error]
  )

  return (
    <div className={className}>
      <Text
        as="label"
        preset="body.sm"
        id={`${name}-label`}
        htmlFor={name}
        className={classNames({
          'mb-[2px] block': true,
          'text-gray-5': !hasError,
          'text-red': hasError,
        })}
      >
        {displayName}
      </Text>

      <div className="relative">
        {/* Number inputs have their own prefix handling */}
        {prefix && type !== 'number' && (
          <Text
            as="div"
            preset="body.md"
            className="absolute left-2 top-[50%] w-4 -translate-y-[50%] text-right"
          >
            {prefix}
          </Text>
        )}

        {type === 'file' &&
          renderFileInput({
            field,
            name,
            hasError,
            ...rest,
          })}

        {type !== 'file' &&
          renderInput({
            field,
            name,
            type,
            prefix,
            isSecretMasked,
            hasError,
            ...rest,
          })}

        {isSecret && field.value && (
          <FormFieldSecretToggle
            name={name}
            isSecretMasked={isSecretMasked}
            setSecretMasked={setSecretMasked}
          />
        )}
      </div>

      {helpText && (
        <Text as="div" preset="body.xs sm:body.sm" className="mt-1 text-gray-2">
          {helpText}
        </Text>
      )}

      <FormError error={hasError ? (meta.error as string) : null} />
    </div>
  )
}

export default FormField
